import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, FileText, Copy } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "payslip",      label: "Payslip" },
  { value: "warning",      label: "Warning" },
  { value: "appreciation", label: "Appreciation" },
  { value: "appointment",  label: "Appointment letter" },
  { value: "leave",        label: "Leave decision" },
  { value: "advance",      label: "Advance decision" },
  { value: "announcement", label: "Announcement" },
  { value: "welcome",      label: "Welcome / onboarding" },
  { value: "custom",       label: "Custom" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const COMMON_PLACEHOLDERS = [
  "{{employee_name}}", "{{employee_id}}", "{{role_label}}", "{{department}}",
  "{{date_of_joining}}", "{{today}}", "{{company_name}}", "{{company_address}}",
  "{{period_label}}", "{{period_month}}", "{{net_payable}}",
  "{{severity}}", "{{reason}}",
  "{{decision}}", "{{decision_note}}", "{{leave_date}}",
  "{{title}}", "{{body}}",
];

const EMPTY = { name: "", category: "custom", subject: "", html_body: "", attach_payslip: false };

export default function EmailTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email/templates");
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byCategory = useMemo(() => {
    const map = {};
    items.forEach((t) => {
      map[t.category] = map[t.category] || [];
      map[t.category].push(t);
    });
    return map;
  }, [items]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t }); setOpen(true); };
  const duplicate = (t) => {
    setEditing(null);
    setForm({ ...EMPTY, name: `${t.name} (copy)`, category: t.category, subject: t.subject, html_body: t.html_body, attach_payslip: !!t.attach_payslip });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.subject.trim()) { toast.error("Subject is required"); return; }
    if (!form.html_body.trim()) { toast.error("Body is required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/email/templates/${editing.id}`, form);
        toast.success("Template updated");
      } else {
        await api.post("/email/templates", form);
        toast.success("Template created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/email/templates/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const insertPlaceholder = (ph) => {
    setForm((f) => ({ ...f, html_body: (f.html_body || "") + " " + ph }));
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Communications</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Email Templates</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Reusable subject + HTML bodies with placeholders. Used by the manual "Send Email" buttons across Payroll, Warnings, Staff, and more.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-zinc-950 hover:bg-zinc-800" data-testid="email-template-new">
          <Plus className="w-4 h-4 mr-1" /> New template
        </Button>
      </div>

      {CATEGORIES.filter((c) => byCategory[c.value]?.length).map((c) => (
        <section key={c.value} className="bg-white border border-zinc-200 rounded-md overflow-hidden">
          <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 label-overline">{c.label}</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest border-b border-zinc-200">
                <th className="py-2 px-4">Name</th>
                <th className="py-2 px-3">Subject</th>
                <th className="py-2 px-3">Attach</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {byCategory[c.value].map((t) => (
                <tr key={t.id} className="border-b border-zinc-100 last:border-0" data-testid={`email-template-${t.id}`}>
                  <td className="py-2.5 px-4">
                    <div className="font-medium">{t.name}</div>
                    {t.is_default && <span className="text-[10px] text-emerald-700 uppercase tracking-widest">Default</span>}
                  </td>
                  <td className="py-2.5 px-3 text-zinc-600 truncate max-w-[420px]">{t.subject}</td>
                  <td className="py-2.5 px-3 text-xs">{t.attach_payslip ? "Payslip PDF" : "—"}</td>
                  <td className="py-2.5 px-3 text-right">
                    <button onClick={() => duplicate(t)} className="p-1.5 hover:bg-zinc-100 rounded" title="Duplicate" data-testid={`email-template-duplicate-${t.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => openEdit(t)} className="p-1.5 hover:bg-zinc-100 rounded ml-1" data-testid={`email-template-edit-${t.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {!t.is_default && (
                      <button onClick={() => remove(t)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded ml-1" data-testid={`email-template-delete-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
      {items.length === 0 && (
        <div className="bg-white border border-dashed border-zinc-200 rounded-md py-16 text-center text-zinc-400">
          <FileText className="w-6 h-6 mx-auto mb-2" /> No templates yet. Hit <em>New template</em> to add one.
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="email-template-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit template" : "New email template"}</DialogTitle>
            <DialogDescription>
              Use placeholders like <code>{"{{employee_name}}"}</code> — they get replaced when the email is actually sent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="email-template-name" />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="email-template-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-template-subject" />
            </div>
            <div>
              <Label className="text-xs">Body (HTML)</Label>
              <Textarea value={form.html_body} onChange={(e) => setForm({ ...form, html_body: e.target.value })} rows={10} className="font-mono text-xs" data-testid="email-template-body" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500 uppercase tracking-widest">Insert placeholder</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {COMMON_PLACEHOLDERS.map((ph) => (
                  <button key={ph} onClick={() => insertPlaceholder(ph)} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 hover:bg-zinc-200 font-mono">{ph}</button>
                ))}
              </div>
            </div>
            {form.category === "payslip" && (
              <div className="flex items-center justify-between border border-zinc-200 rounded-md p-3 bg-zinc-50">
                <div>
                  <div className="text-sm font-medium">Attach payslip PDF automatically</div>
                  <div className="text-[11px] text-zinc-500">When this template is used, the matching month's payslip is attached.</div>
                </div>
                <Switch
                  checked={!!form.attach_payslip}
                  onCheckedChange={(v) => setForm({ ...form, attach_payslip: v })}
                  data-testid="email-template-attach-payslip"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="email-template-save">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} {editing ? "Save changes" : "Create template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { CATEGORY_LABEL };
